import { useState, useEffect } from "react";
import { resetPassword } from "@auth/auth";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "react-router-dom";
import { InputField, Button } from "../common";

const resetPasswordSchema = z.object({
  email: z.string().email("Invalid email address"),
});

export const LupaPassword = () => {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(resetPasswordSchema),
  });

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  const handleResetPassword = async (data) => {
    setLoading(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      await resetPassword(data.email);
      setSuccessMessage("A password reset link has been sent to your email.");
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-cover bg-center px-4"
      style={{ backgroundImage: "url('/bg/bghero5.jpg')" }}
    >
      <div className="fixed top-0 left-0 w-full h-full bg-cyan-900 bg-opacity-40 backdrop-blur-sm"></div>
      <motion.div
        className="relative z-0 max-w-4xl w-full mx-auto bg-white bg-opacity-40 shadow-lg backdrop-blur-md grid md:grid-cols-2 rounded-xl overflow-hidden"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, ease: "easeInOut" }}
      >
        <div
          className="hidden md:flex flex-col justify-center text-white bg-cover bg-center p-8 h-full relative rounded-tl-xl rounded-bl-xl"
          style={{ backgroundImage: "url('/bg/bghero1.jpg')" }}
        >
          <div className="absolute inset-0 bg-cyan-900 bg-opacity-80 backdrop-blur-sm z-0 rounded-tl-xl rounded-bl-xl"></div>

          <motion.h1
            className="text-4xl font-bold mb-4 drop-shadow-md z-0 relative"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            Welcome
          </motion.h1>
          <motion.p
            className="text-lg drop-shadow-md z-20 relative"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut", delay: 0.3 }}
          >
            Sign in to access your account and enjoy the best experience with us.
          </motion.p>
        </div>

        <div className="flex flex-col p-6 z-10">
          <h2 className="text-2xl font-semibold text-center mb-6 text-white">
            Forgot Password
          </h2>
          <form
            onSubmit={handleSubmit(handleResetPassword)}
            className="flex flex-col gap-4 text-black"
          >
            <InputField
              label="Email"
              name="email"
              type="email"
              placeholder="Enter your email"
              {...register("email")}
              error={errors.email?.message}
              labelColor="text-white"
            />

            {errorMessage && (
              <p className="text-sm text-white text-center -mt-2">
                {errorMessage}
              </p>
            )}
            {successMessage && (
              <p className="text-sm text-white text-center -mt-2">
                {successMessage}
              </p>
            )}

            <Button
              type="submit"
              variant="primary"
              className="mt-4 w-full"
              disabled={isSubmitting || loading}
            >
              {isSubmitting || loading ? "Sending..." : "Send Reset Link"}
            </Button>
          </form>
        </div>
      </motion.div>
    </div>
  );
};
